import { Component, OnInit, ViewChild } from "@angular/core";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { Sort } from "@angular/material/sort";
import { ActivatedRoute } from "@angular/router";
import { Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { PersonalProtectionDataService } from "src/app/share/data-service/personal-perotection/personal-protection.data.service";
import { CommonSelectionInterface } from "src/app/share/interface/common.interface";
import {
    CelcomdigiPolicyDetail,
    CelcomdigiPolicyClaimListing
} from "src/app/share/interface/personal-protection/pp-celcomdigi.interface";
import { PolicyStatus } from "../../../share/constants/celcomdigi/policy-status";
import { ClaimStatus } from "../../../share/constants/celcomdigi/claim-status";
import { AlertService } from "../../../share/service/alert.service";

@Component({
  selector: "app-celcomdigi-details",
  templateUrl: "./celcomdigi-details.component.html",
  styleUrls: ["./celcomdigi-details.component.scss"]
})
export class CelcomdigiDetailsComponent implements OnInit {
  id: string;

  sortDirection: "asc" | "desc" = "desc";
  sortIndex: number = 0;

  searchText: string;
  searchChange: Subject<void> = new Subject<void>();

  statusList: CommonSelectionInterface[] = [];
  filterStatusList: CommonSelectionInterface[] = [];
  selectedStatusList: string[] = [];

  typeList: CommonSelectionInterface[] = [];
  filterTypeList: CommonSelectionInterface[] = [];
  selectedTypeList: string[] = [];

  dataSource: CelcomdigiPolicyClaimListing[] = [];
  isLoading = false;
  claimListColumns: string[] = [
    "createdDate",
    "claimRefNo",
    "UI_claimType",
    "UI_claimStatus"
  ];

  length = 0;

  details: CelcomdigiPolicyDetail;

  @ViewChild("paginator") paginator;

  constructor(
    private activatedRoute: ActivatedRoute,
    private alertService: AlertService,
    private personalprotectionDataService: PersonalProtectionDataService,
    public bottomSheet: MatBottomSheet
  ) {}

  ngOnInit(): void {
    if (!this.activatedRoute.snapshot.queryParams.id) {
      window.history.back();
      return;
    }

    this.id = this.activatedRoute.snapshot.queryParams.id;
    this.getClaimListing();

    this.searchChange.pipe(debounceTime(500)).subscribe(() => {
      this.filter();
    });
  }

  searchChanged(): void {
    // if (this.searchText)
    this.searchChange.next();
  }

  filterStatusSelection(searchValue: string = null): void {
    if (searchValue) {
      let txt = searchValue.toLowerCase().trim();
      this.filterStatusList = this.statusList.filter((x) =>
        x.name.toLowerCase().includes(txt)
      );
    } else {
      this.filterStatusList = Object.assign([], this.statusList);
    }
  }

  isSelectedAllStatus(): boolean {
    return this.selectedStatusList.length == this.statusList.length;
  }

  isIndeterminateStatus(): boolean {
    if (this.isSelectedAllStatus()) return false;
    return this.selectedStatusList.length > 0;
  }

  toggleSelectAllStatus(val: boolean, hasFilter: boolean = true): void {
    this.selectedStatusList = [];
    if (val) {
      this.selectedStatusList = this.statusList.map((y) => y.id);
      if (hasFilter) {
        this.filter();
      }
    } else {
      this.dataSource = [];
      this.length = 0;
    }
  }

  getStatusDisplay(): string {
    if (this.selectedStatusList.length == 1)
      return this.statusList.find((x) => x.id == this.selectedStatusList[0])
        .name;
    else if (this.isSelectedAllStatus()) return "All Status";
    else return `${this.selectedStatusList.length} status selected`;
  }

  resetStatus(): void {
    event.stopImmediatePropagation();
    this.toggleSelectAllStatus(true);
  }

  filterTypeSelection(searchValue: string = null): void {
    if (searchValue) {
      let txt = searchValue.toLowerCase().trim();
      this.filterTypeList = this.typeList.filter((x) =>
        x.name.toLowerCase().includes(txt)
      );
    } else {
      this.filterTypeList = Object.assign([], this.typeList);
    }
  }

  isSelectedAllType(): boolean {
    return this.selectedTypeList.length == this.typeList.length;
  }

  isIndeterminateType(): boolean {
    if (this.isSelectedAllType()) return false;
    return this.typeList.length > 0;
  }

  toggleSelectAllType(val: boolean, hasFilter: boolean = true): void {
    this.selectedTypeList = [];
    if (val) {
      this.selectedTypeList = this.typeList.map((y) => y.id);
      if (hasFilter) {
        this.filter();
      }
    } else {
      this.dataSource = [];
      this.length = 0;
    }
  }

  getTypeDisplay(): string {
    if (this.typeList.length == 1)
      return this.typeList.find((x) => x.id == this.selectedTypeList[0]).name;
    else if (this.isSelectedAllType()) return "All Type";
    else return `${this.selectedTypeList.length} type selected`;
  }

  resetType(): void {
    event.stopImmediatePropagation();
    this.toggleSelectAllType(true);
  }

  filter(): void {
    this.getPolicyClaimListing();
  }

  sortChange(sortState: Sort): void {
    this.sortDirection = null;
    this.sortIndex = -1;
    if (sortState.direction) {
      this.sortDirection = sortState.direction;
      this.sortIndex = this.claimListColumns.findIndex(
        (x) => x == sortState.active
      );
    }
    if (this.dataSource.length == 0) return;

    this.getPolicyClaimListing();
  }

  getPolicyStatusCss(policyStatus: string): string {
    switch (policyStatus) {
      case PolicyStatus.Active:
        return "green";
      case PolicyStatus.Expired:
      case PolicyStatus.Cancelled:
        return "red";
      case PolicyStatus.Refunded:
      case PolicyStatus.Lapsed:
      default:
        return "orange";
    }
  }
  
  getClaimStatusCss(claimStatus: string): string {
    switch (claimStatus) {
        case ClaimStatus.New:
          return "grey";
        case ClaimStatus.Approved:
          return "green";
        case ClaimStatus.Rejected:
          return "red";
        default:
          return "orange";
      }
  }

  private getData(): void {
    this.personalprotectionDataService.getCelcomdigiPolicyDetails(this.id).subscribe({
      next: (x: CelcomdigiPolicyDetail) => {
        this.details = x;
      },
      error: (err) => {
        console.log(err);
      }
    });
  }

  private getPolicyClaimListing(): void {
    this.isLoading = true;
    this.dataSource = [];
    this.length = 0;

    this.personalprotectionDataService
      .getCelcomdigiPolicyClaimListing(
        this.id,
        this.searchText,
        this.sortIndex,
        this.sortDirection,
        this.selectedStatusList,
        this.selectedTypeList
      )
      .subscribe({
        next: (data: CelcomdigiPolicyClaimListing[]) => (this.dataSource = data),
        error: (err) => this.alertService.openSnackBar(err.error)
      })
      .add(() => (this.isLoading = false));
  }

  private getClaimListing(): void {
    this.statusList = this.personalprotectionDataService.getCelcomdigiClaimStatusList();
    this.typeList = this.personalprotectionDataService.getCelcomdigiClaimTypeList();

    this.filterStatusSelection();
    this.filterTypeSelection();
    this.toggleSelectAllStatus(true, false);
    this.toggleSelectAllType(true, false);

    this.getData();
    this.getPolicyClaimListing();
  }
}
